import { CheckIcon, DocumentIcon } from '@sketchnote/icons';
import { ProgressBar } from '@sketchnote/shared-components';
import classNames from 'classnames';
import * as React from 'react';

import { Avatar, AvatarGroup } from '../avatar';
import { Image } from '../image';
import { MenuItemProps, MoreActionMenu } from '../menu';
import { SharedWithProps } from '../search-dialog/search-dialog.types';
import { Tag, TagDataType } from '../tags/tags.component';

const { useCallback } = React;
export interface CreationProps {
  title: string;
  modified: {
    author: string;
    timing: string;
  };
  tag: TagDataType;
  tasks: {
    total: number;
    completed: number;
  };
  image: {
    url: string;
    alt: string;
  };
  type?: 'docs' | 'creations';
  onClick?: (args: Partial<CreationProps>) => void | undefined;
  sharedWith?: SharedWithProps[];
  menuItems: { label: string; value: string; icon?: JSX.Element }[];
  onClickMenuItem?: (menuitem: MenuItemProps, e: React.KeyboardEvent<HTMLDivElement>) => void;
  className?: string;
  lessonSlug: string;
  creationId: string;
}

export const Creation = ({
  title,
  modified,
  tag,
  tasks,
  type = 'creations',
  onClick,
  sharedWith,
  menuItems,
  onClickMenuItem,
  className,
  image,
  lessonSlug,
  creationId,
}: CreationProps): JSX.Element => {
  const percentage = parseFloat(((tasks.completed / tasks.total) * 100).toFixed());
  const isCompleted = percentage === 100;
  const isDocs = type === 'docs';

  const handleKeyEnter: React.KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.key === 'Enter') {
      onClick?.({ creationId, lessonSlug });
    }
  }, []);

  function handleOnClick() {
    onClick?.({ creationId, lessonSlug });
  }

  return (
    <div
      className={classNames({
        'flex border rounded-md hover:shadow-md transition-all duration-300 bg-white': true,
        [className || '']: !!className,
      })}
      tabIndex={0}
      id="snCreationComponent"
      aria-label="Creation"
      title="Creation"
      role="button"
      onKeyDown={handleKeyEnter}
      onClick={handleOnClick}
    >
      <div className={classNames({ 'flex items-center ml-24': isDocs })}>
        {!isDocs ? (
          <div className="border-r relative" style={{ width: '174px', height: '142px' }}>
            <Image src={image?.url} className="m-auto max-w-full max-h-full" alt={image?.alt} />
          </div>
        ) : (
          <div>
            <DocumentIcon wrapperClass="bg-gray-100 p-7 text-primary-300 rounded-sm" />
          </div>
        )}
      </div>
      <div
        className={classNames({
          'w-full pt-24 px-24 pb-20 flex flex-col justify-between relative': true,
          'w-full': isDocs,
        })}
      >
        <div>
          <div className="flex leading-20 align-middle items-center">
            <h4 className={classNames({ 'mr-8 mb-4': true, 'mb-4': !isDocs })}>{title}</h4>
            {!isDocs ? <Tag color={tag.color}>{tag.label}</Tag> : null}
          </div>

          <p className="text-gray-500 text-xs">
            {`Edited by ${modified.author} ${modified.timing}`}
          </p>
        </div>
        {!isDocs ? (
          <div className="flex items-center">
            <span className="font-medium text-sm mr-8 text-black">
              {isCompleted ? 'Completed ' : `${tasks.completed}/${tasks.total} microtasks`}
            </span>
            {!isCompleted ? (
              <div className="w-96 mt-5">
                <ProgressBar value={percentage} />
              </div>
            ) : (
              <CheckIcon className="text-green-600" />
            )}
          </div>
        ) : null}
        <div className="absolute top-24 right-24 flex">
          <div className="mr-24 flex items-center">
            {sharedWith?.length ? (
              <AvatarGroup maxCount={2}>
                {sharedWith?.map((member) => (
                  <Avatar
                    name={member.name}
                    src={member.photoUrl}
                    size="sm"
                    type="photo"
                    key={member.name}
                  />
                ))}
              </AvatarGroup>
            ) : null}
          </div>
          <div className="flex items-center text-gray-500">
            <MoreActionMenu
              menuItems={menuItems}
              onClickMenuItem={onClickMenuItem as any}
              placement="bottom"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
